import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Alert, Input, Checkbox, Button, Divider, Typography, Spin, Form } from 'antd';
import { PlusOutlined, MinusCircleOutlined, UndoOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { Entitlement, OrgEntitlement } from '../../types';
import { UpdateOperation, EntityType } from './types';
import { useFieldConfig } from '../../contexts/FieldConfigContext';
import { noLeadingTrailingSpaces } from '../../utils/utils';

const { Title } = Typography;

type EntitlementAction = 'update' | 'delete' | 'insert';

interface EntitlementRow extends Record<string, unknown> {
  id?: number;
  name: string;
  value: string;
  description?: string;
  needs_quantity: boolean;
  unit?: string;
  action: EntitlementAction;
  requireDescription?: boolean;
}

interface OrgEntitlementRow extends Record<string, unknown> {
  id?: number;
  name: string;
  value: string;
  description?: string;
  needs_quantity: boolean;
  unit?: string;
  access_type: string;
  is_service: boolean;
  action: EntitlementAction;
  requireDescription?: boolean;
}

interface EntitlementTableEditorProps {
  productId: number;
  productName: string;
  onUpdate: (update: UpdateOperation) => void;
  existingUpdates?: UpdateOperation[];
}

export interface EntitlementTableEditorHandle {
  /** Validates all form fields. Resolves true if valid, false if there are errors. */
  validate: () => Promise<boolean>;
}

const EntitlementTableEditor = forwardRef<EntitlementTableEditorHandle, EntitlementTableEditorProps>(({
  productId,
  productName,
  onUpdate,
  existingUpdates = [],
}, ref) => {
  const { isFieldDisabled } = useFieldConfig();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const lastObservedRows = useRef<{ entitlements: EntitlementRow[]; orgEntitlements: OrgEntitlementRow[] }>({
    entitlements: [],
    orgEntitlements: [],
  });

  useImperativeHandle(ref, () => ({
    validate: async () => {
      try {
        await form.validateFields();
        return true;
      } catch {
        return false;
      }
    },
  }));
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [orgEntitlements, setOrgEntitlements] = useState<OrgEntitlement[]>([]);
  const [nextEntitlementId, setNextEntitlementId] = useState<number>(1);
  const [nextOrgEntitlementId, setNextOrgEntitlementId] = useState<number>(1);

  useEffect(() => {
    const loadEntitlements = async () => {
      setLoading(true);
      try {
        // Fetch ALL entitlements (unfiltered) to compute global max IDs
        const [allEnts, allOrgEnts] = await Promise.all([
          apiService.getEntitlements(),
          apiService.getOrgEntitlements(),
        ]);

        // Compute global max IDs
        const maxEntId = allEnts.length > 0 ? Math.max(...allEnts.map((e) => e.id ?? 0)) : 0;
        const maxOrgEntId = allOrgEnts.length > 0 ? Math.max(...allOrgEnts.map((e) => e.id ?? 0)) : 0;
        setNextEntitlementId(maxEntId + 1);
        setNextOrgEntitlementId(maxOrgEntId + 1);

        // Filter for current product
        const ents = allEnts.filter((e) => e.product_id === productId);
        const orgEnts = allOrgEnts.filter((oe) => oe.product_id === productId);

        setEntitlements(ents);
        setOrgEntitlements(orgEnts);

        // Check if there are existing edits to restore
        const pendingUpdate = existingUpdates.find(
          (u) => u.entityType === EntityType.ENTITLEMENT && u.entityId === String(productId),
        );

        if (pendingUpdate) {
          const changedEnts = (pendingUpdate.updates.entitlements || []) as EntitlementRow[];
          const changedOrgEnts = (pendingUpdate.updates.orgEntitlements || []) as OrgEntitlementRow[];

          // Rebuild full list: start with originals tagged 'update', then overlay changes
          const restoredEnts: EntitlementRow[] = ents.map((e) => {
            const changed = changedEnts.find((c) => c.id === e.id);
            if (changed) {
              return { requireDescription: true, ...changed };
            }
            return { requireDescription: true, ...e, action: 'update' as EntitlementAction };
          });
          // Add any inserted rows (action === 'insert') that aren't in originals
          changedEnts
            .filter((c) => c.action === 'insert')
            .forEach((inserted) => {
              restoredEnts.push({ ...inserted });
            });

          const restoredOrgEnts: OrgEntitlementRow[] = orgEnts.map((oe) => {
            const changed = changedOrgEnts.find((c) => c.id === oe.id);
            if (changed) {
              return { requireDescription: true, ...changed };
            }
            return { requireDescription: true, ...oe, action: 'update' as EntitlementAction };
          });
          changedOrgEnts
            .filter((c) => c.action === 'insert')
            .forEach((inserted) => {
              restoredOrgEnts.push({ ...inserted });
            });

          // Update nextId counters to account for previously inserted rows
          const maxRestoredEntId = restoredEnts.length > 0
            ? Math.max(...restoredEnts.map((e) => e.id ?? 0))
            : maxEntId;
          const maxRestoredOrgEntId = restoredOrgEnts.length > 0
            ? Math.max(...restoredOrgEnts.map((e) => e.id ?? 0))
            : maxOrgEntId;
          setNextEntitlementId(Math.max(maxEntId + 1, maxRestoredEntId + 1));
          setNextOrgEntitlementId(Math.max(maxOrgEntId + 1, maxRestoredOrgEntId + 1));

          lastObservedRows.current = {
            entitlements: restoredEnts.map((row) => ({ ...row })),
            orgEntitlements: restoredOrgEnts.map((row) => ({ ...row })),
          };
          form.setFieldsValue({
            entitlements: restoredEnts,
            orgEntitlements: restoredOrgEnts,
          });
        } else {
          // No existing edits — tag all rows as 'update'
          const taggedEnts = ents.map((e) => ({ requireDescription: true, ...e, action: 'update' as EntitlementAction }));
          const taggedOrgEnts = orgEnts.map((oe) => ({ requireDescription: true, ...oe, action: 'update' as EntitlementAction }));
          lastObservedRows.current = {
            entitlements: taggedEnts.map((row) => ({ ...row })),
            orgEntitlements: taggedOrgEnts.map((row) => ({ ...row })),
          };
          form.setFieldsValue({
            entitlements: taggedEnts,
            orgEntitlements: taggedOrgEnts,
          });
        }
      } catch {
        setEntitlements([]);
        setOrgEntitlements([]);
      } finally {
        setLoading(false);
      }
    };
    void loadEntitlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, form]);

  // Keep matching standard/org entitlement rows in sync for this product.
  // Match against the original records so a rename/value change does not break
  // the link before the counterpart can be found.
  const syncMatchingEntitlementRows = () => {
    const sharedFields = ['name', 'value', 'description', 'needs_quantity', 'unit'] as const;
    type MatchKeyFields = Pick<Entitlement, 'id' | 'product_id' | 'name' | 'value' | 'needs_quantity' | 'unit'>;
    const sameId = (left: number | string | undefined, right: number | string | undefined) =>
      left !== undefined && right !== undefined && String(left) === String(right);
    const sharedKey = (row: MatchKeyFields) => JSON.stringify([
      String(row.product_id ?? productId),
      row.name ?? '',
      row.value ?? '',
      Boolean(row.needs_quantity),
      row.unit ?? '',
    ]);
    const productIdentityKey = (row: MatchKeyFields) =>
      JSON.stringify([String(row.product_id ?? productId), row.name ?? '', row.value ?? '']);

    const values = form.getFieldsValue();
    const currentEntitlements: EntitlementRow[] = [...(values.entitlements || [])];
    const currentOrgEntitlements: OrgEntitlementRow[] = [...(values.orgEntitlements || [])];
    const findChangedIndexes = <T extends EntitlementRow | OrgEntitlementRow>(
      current: T[],
      previous: T[],
    ) => current.flatMap((row, index) => {
        if (row.id === undefined) { return []; }
        const oldRow = previous.find((candidate) => sameId(candidate.id, row.id));
        if (!oldRow) { return []; }
        return sharedFields.some((field) => (row[field] ?? '') !== (oldRow[field] ?? '')) ? [index] : [];
      });
    const changedEntitlementIndexes = findChangedIndexes(currentEntitlements, lastObservedRows.current.entitlements);
    const changedOrgEntitlementIndexes = findChangedIndexes(currentOrgEntitlements, lastObservedRows.current.orgEntitlements);
    let entitlementsChanged = false;
    let orgEntitlementsChanged = false;

    const findCounterpart = (
      source: MatchKeyFields,
      sourceRows: MatchKeyFields[],
      counterpartRows: MatchKeyFields[],
    ): MatchKeyFields | undefined => {
      const identity = productIdentityKey(source);
      const sourceMatches = sourceRows.filter((row) => productIdentityKey(row) === identity);
      const exactSourceMatches = sourceMatches.filter((row) => sharedKey(row) === sharedKey(source));
      if (sourceMatches.length !== 1 && exactSourceMatches.length !== 1) { return undefined; }

      const candidates = counterpartRows.filter((row) => productIdentityKey(row) === identity);
      if (candidates.length === 1) { return candidates[0]; }
      const exactMatches = candidates.filter((row) => sharedKey(row) === sharedKey(source));
      return exactMatches.length === 1 ? exactMatches[0] : undefined;
    };

    const mirrorFields = (
      source: EntitlementRow | OrgEntitlementRow,
      target: EntitlementRow | OrgEntitlementRow,
    ) => ({
      ...target,
      name: source.name,
      value: source.value,
      description: source.description,
      needs_quantity: source.needs_quantity,
      unit: source.unit,
    });

    changedEntitlementIndexes.forEach((index) => {
      const source = currentEntitlements[index];
      if (!source || source.action !== 'update' || source.id === undefined) { return; }
      const originalSource = entitlements.find((row) => sameId(row.id, source.id));
      if (!originalSource) { return; }
      const counterpartOriginal = findCounterpart(originalSource, entitlements, orgEntitlements);
      if (!counterpartOriginal) { return; }
      const counterpartIndex = currentOrgEntitlements.findIndex((row) => sameId(row.id, counterpartOriginal.id));
      const counterpart = currentOrgEntitlements[counterpartIndex];
      if (!counterpart || counterpart.action === 'delete') { return; }
      currentOrgEntitlements[counterpartIndex] = mirrorFields(source, counterpart) as OrgEntitlementRow;
      orgEntitlementsChanged = true;
    });

    changedOrgEntitlementIndexes.forEach((index) => {
      const source = currentOrgEntitlements[index];
      if (!source || source.action !== 'update' || source.id === undefined) { return; }
      const originalSource = orgEntitlements.find((row) => sameId(row.id, source.id));
      if (!originalSource) { return; }
      const counterpartOriginal = findCounterpart(originalSource, orgEntitlements, entitlements);
      if (!counterpartOriginal) { return; }
      const counterpartIndex = currentEntitlements.findIndex((row) => sameId(row.id, counterpartOriginal.id));
      const counterpart = currentEntitlements[counterpartIndex];
      if (!counterpart || counterpart.action === 'delete') { return; }
      currentEntitlements[counterpartIndex] = mirrorFields(source, counterpart) as EntitlementRow;
      entitlementsChanged = true;
    });

    if (entitlementsChanged || orgEntitlementsChanged) {
      form.setFieldsValue({
        ...(entitlementsChanged ? { entitlements: currentEntitlements } : {}),
        ...(orgEntitlementsChanged ? { orgEntitlements: currentOrgEntitlements } : {}),
      });
    }
    const latestValues = form.getFieldsValue();
    lastObservedRows.current = {
      entitlements: (latestValues.entitlements || []).map((row: EntitlementRow) => ({ ...row })),
      orgEntitlements: (latestValues.orgEntitlements || []).map((row: OrgEntitlementRow) => ({ ...row })),
    };
  };

  // Emit update whenever form values change
  const handleValuesChange = () => {
    syncMatchingEntitlementRows();
    const values = form.getFieldsValue();
    const allEntitlements: EntitlementRow[] = values.entitlements || [];
    const allOrgEntitlements: OrgEntitlementRow[] = values.orgEntitlements || [];

    // Only include rows that have been changed, deleted, or inserted
    const changedEntitlements = allEntitlements.filter((e: EntitlementRow) => {
      if (e.action === 'insert' || e.action === 'delete') {
        return true;
      }
      // For 'update' rows, check if any field actually changed from original
      const original = entitlements.find((orig) => String(orig.id) === String(e.id));
      if (!original) {
        return true;
      }
      return (
        e.name !== original.name ||
        e.value !== original.value ||
        (e.description ?? '') !== (original.description ?? '') ||
        e.needs_quantity !== original.needs_quantity ||
        (e.unit ?? '') !== (original.unit ?? '')
      );
    });

    const changedOrgEntitlements = allOrgEntitlements.filter((e: OrgEntitlementRow) => {
      if (e.action === 'insert' || e.action === 'delete') {
        return true;
      }
      const original = orgEntitlements.find((orig) => String(orig.id) === String(e.id));
      if (!original) {
        return true;
      }
      return (
        e.name !== original.name ||
        e.value !== original.value ||
        (e.description ?? '') !== (original.description ?? '') ||
        e.needs_quantity !== original.needs_quantity ||
        (e.unit ?? '') !== (original.unit ?? '') ||
        e.access_type !== original.access_type ||
        (e.is_service ?? false) !== (original.is_service ?? false)
      );
    });

    const update: UpdateOperation = {
      entityType: EntityType.ENTITLEMENT,
      entityId: String(productId),
      entityName: `Entitlements for ${productName}`,
      updates: {
        entitlements: changedEntitlements.map(({ requireDescription: _rd, ...rest }) => rest),
        orgEntitlements: changedOrgEntitlements.map(({ requireDescription: _rd, ...rest }) => rest),
      },
      originalData: {
        entitlements,
        orgEntitlements,
      },
    };
    onUpdate(update);
  };

  // Mark a row as deleted (soft delete) instead of removing from form
  const handleDeleteRow = (listName: string, index: number) => {
    const values = form.getFieldsValue();
    const list = [...(values[listName] || [])];
    const row = list[index];
    if (row.action === 'insert') {
      // New row that hasn't been saved — just remove it
      list.splice(index, 1);
    } else {
      // Existing row — mark as deleted
      list[index] = { ...row, action: 'delete' };
    }
    form.setFieldsValue({ [listName]: list });
    handleValuesChange();
  };

  // Undo a delete — restore to 'update'
  const handleUndoDelete = (listName: string, index: number) => {
    const values = form.getFieldsValue();
    const list = [...(values[listName] || [])];
    list[index] = { ...list[index], action: 'update' };
    form.setFieldsValue({ [listName]: list });
    handleValuesChange();
  };

  // Add a new row tagged with action: 'insert' and auto-assign the next available ID
  const handleAddRow = (listName: string, defaults: Record<string, unknown>) => {
    const values = form.getFieldsValue();
    let assignedId: number;
    if (listName === 'entitlements') {
      assignedId = nextEntitlementId;
      setNextEntitlementId((prev) => prev + 1);
    } else {
      assignedId = nextOrgEntitlementId;
      setNextOrgEntitlementId((prev) => prev + 1);
    }
    const list = [...(values[listName] || []), { ...defaults, id: assignedId, action: 'insert' }];
    form.setFieldsValue({ [listName]: list });
    handleValuesChange();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  const hasStandardEntitlements = entitlements.length > 0;
  const hasOrgEntitlements = orgEntitlements.length > 0;

  return (
    <div>
      <Alert
        message="Update Product Entitlements"
        description={`Managing entitlements for the selected product. Found ${hasStandardEntitlements ? entitlements.length + ' standard' : '0 standard'} and ${hasOrgEntitlements ? orgEntitlements.length + ' org' : '0 org'} entitlements. Last Entitlement ID: ${nextEntitlementId - 1} | Last Org Entitlement ID: ${nextOrgEntitlementId - 1}`}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form form={form} onValuesChange={handleValuesChange}>
        {/* Standard Entitlements */}
        {hasStandardEntitlements && (
          <>
            <Title level={5} style={{ marginBottom: 16 }}>Standard Entitlements</Title>
            <Form.List name="entitlements">
              {(fields) => (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--bg-elevated)', borderBottom: '2px solid var(--border-color)' }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, width: 70 }}>ID</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Name *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Value *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 110 }} title="Require description before submitting. Uncheck to allow empty description (entitlement will not appear on SCC UI).">Req. Desc</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Needs Quantity</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Unit</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 60 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map(({ key, name, ...restField }) => {
                        const rowAction = form.getFieldValue(['entitlements', name, 'action']) as EntitlementAction;
                        const isDeleted = rowAction === 'delete';
                        const isNewRow = rowAction === 'insert';
                        const rowStyle: React.CSSProperties = {
                          borderBottom: '1px solid var(--border-color)',
                          opacity: isDeleted ? 0.45 : 1,
                          backgroundColor: isDeleted ? 'rgba(255, 77, 79, 0.1)' : rowAction === 'insert' ? 'rgba(82, 196, 26, 0.1)' : undefined,
                        };
                        return (
                          <React.Fragment key={key}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) =>
                                prev?.entitlements?.[name]?.requireDescription !== curr?.entitlements?.[name]?.requireDescription
                              }
                            >
                              {({ getFieldValue: getVal }) => {
                                const reqDesc = getVal(['entitlements', name, 'requireDescription']);
                                const isRequired = reqDesc !== false;
                                return !isRequired ? (
                                  <tr>
                                    <td colSpan={8} style={{ padding: '7px 8px 2px', borderBottom: 'none' }}>
                                      <Alert
                                        message="⚠️ No description — this entitlement will not appear on SCC UI"
                                        type="warning"
                                        style={{ fontSize: 12, padding: '4px 10px', borderRadius: 4, marginBottom: 2 }}
                                      />
                                    </td>
                                  </tr>
                                ) : null;
                              }}
                            </Form.Item>
                            <tr style={rowStyle}>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'id']} style={{ marginBottom: 0 }} noStyle>
                                  <Input disabled style={{ width: 60, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'name']} rules={[{ required: !isDeleted, message: 'Required' }, noLeadingTrailingSpaces]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="e.g., storage_limit" disabled={isDeleted || (!isNewRow && isFieldDisabled(EntityType.ENTITLEMENT, 'name'))} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'value']} rules={[{ required: !isDeleted, message: 'Required' }, noLeadingTrailingSpaces]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="e.g., 1TB" disabled={isDeleted || (!isNewRow && isFieldDisabled(EntityType.ENTITLEMENT, 'value'))} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item
                                  noStyle
                                  shouldUpdate={(prev, curr) =>
                                    prev?.entitlements?.[name]?.requireDescription !== curr?.entitlements?.[name]?.requireDescription
                                  }
                                >
                                  {({ getFieldValue: getVal }) => {
                                    const reqDesc = getVal(['entitlements', name, 'requireDescription']);
                                    const descRequired = !isDeleted && reqDesc !== false;
                                    return (
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'description']}
                                        rules={[{ required: descRequired, message: 'Description is required' }, noLeadingTrailingSpaces]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Input placeholder="e.g., description" disabled={isDeleted} />
                                      </Form.Item>
                                    );
                                  }}
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                <Form.Item {...restField} name={[name, 'requireDescription']} valuePropName="checked" style={{ marginBottom: 0 }}>
                                  <Checkbox disabled={isDeleted} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <Form.Item {...restField} name={[name, 'needs_quantity']} valuePropName="checked" style={{ marginBottom: 0 }}>
                                  <Checkbox disabled={isDeleted || (!isNewRow && isFieldDisabled(EntityType.ENTITLEMENT, 'needs_quantity'))} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'unit']} style={{ marginBottom: 0 }} rules={[noLeadingTrailingSpaces]}>
                                  <Input placeholder="e.g., GB" disabled={isDeleted} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: 0, width: 0, overflow: 'hidden' }}>
                                <Form.Item {...restField} name={[name, 'action']} style={{ marginBottom: 0 }} noStyle>
                                  <Input type="hidden" />
                                </Form.Item>
                                <Form.Item {...restField} name={[name, 'product_id']} style={{ marginBottom: 0 }} noStyle>
                                  <Input type="hidden" />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                {isDeleted ? (
                                  <UndoOutlined
                                    onClick={() => !(isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) && handleUndoDelete('entitlements', name)}
                                    style={{
                                      color: (isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? '#d9d9d9' : '#1890ff',
                                      fontSize: 18,
                                      cursor: (isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? 'not-allowed' : 'pointer',
                                      opacity: (isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? 0.5 : 1
                                    }}
                                    title={(isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? 'Undo action disabled' : 'Undo delete'}
                                  />
                                ) : (
                                  <MinusCircleOutlined
                                    onClick={() => !(isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) && handleDeleteRow('entitlements', name)}
                                    style={{
                                      color: (isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? '#d9d9d9' : '#ff4d4f',
                                      fontSize: 18,
                                      cursor: (isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? 'not-allowed' : 'pointer',
                                      opacity: (isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? 0.5 : 1
                                    }}
                                    title={(isFieldDisabled(EntityType.ENTITLEMENT, 'delete') && !isNewRow) ? 'Delete action disabled' : 'Delete'}
                                  />
                                )}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  <Button
                    type="dashed"
                    onClick={() => handleAddRow('entitlements', { name: '', value: '', description: '', needs_quantity: false, unit: '', product_id: productId, requireDescription: true })}
                    block
                    icon={<PlusOutlined />}
                    disabled={isFieldDisabled(EntityType.ENTITLEMENT, 'add')}
                  >
                    Add Entitlement
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}

        {/* Organization Entitlements */}
        {hasOrgEntitlements && (
          <>
            {hasStandardEntitlements && <Divider style={{ margin: '24px 0' }} />}
            <Title level={5} style={{ marginBottom: 16 }}>Organization Entitlements</Title>
            <Form.List name="orgEntitlements">
              {(fields) => (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--bg-elevated)', borderBottom: '2px solid var(--border-color)' }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, width: 70 }}>ID</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Name *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Value *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 110 }} title="Require description before submitting. Uncheck to allow empty description (entitlement will not appear on SCC UI).">Req. Desc</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Access Type *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Needs Quantity</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Unit</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Is Service</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 60 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map(({ key, name, ...restField }) => {
                        const rowAction = form.getFieldValue(['orgEntitlements', name, 'action']) as EntitlementAction;
                        const isDeleted = rowAction === 'delete';
                        const isNewRow = rowAction === 'insert';
                        const rowStyle: React.CSSProperties = {
                          borderBottom: '1px solid var(--border-color)',
                          opacity: isDeleted ? 0.45 : 1,
                          backgroundColor: isDeleted ? 'rgba(255, 77, 79, 0.1)' : rowAction === 'insert' ? 'rgba(82, 196, 26, 0.1)' : undefined,
                        };
                        return (
                          <React.Fragment key={key}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) =>
                                prev?.orgEntitlements?.[name]?.requireDescription !== curr?.orgEntitlements?.[name]?.requireDescription
                              }
                            >
                              {({ getFieldValue: getVal }) => {
                                const reqDesc = getVal(['orgEntitlements', name, 'requireDescription']);
                                const isRequired = reqDesc !== false;
                                return !isRequired ? (
                                  <tr>
                                    <td colSpan={10} style={{ padding: '7px 8px 2px', borderBottom: 'none' }}>
                                      <Alert
                                        message="⚠️ No description — this entitlement will not appear on SCC UI"
                                        type="warning"
                                        style={{ fontSize: 12, padding: '4px 10px', borderRadius: 4, marginBottom: 2 }}
                                      />
                                    </td>
                                  </tr>
                                ) : null;
                              }}
                            </Form.Item>
                            <tr style={rowStyle}>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'id']} style={{ marginBottom: 0 }} noStyle>
                                  <Input disabled style={{ width: 60, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'name']} rules={[{ required: !isDeleted, message: 'Required' }, noLeadingTrailingSpaces]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="e.g., feature_access" disabled={isDeleted || (!isNewRow && isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'name'))} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'value']} rules={[{ required: !isDeleted, message: 'Required' }, noLeadingTrailingSpaces]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="e.g., premium" disabled={isDeleted || (!isNewRow && isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'value'))} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item
                                  noStyle
                                  shouldUpdate={(prev, curr) =>
                                    prev?.orgEntitlements?.[name]?.requireDescription !== curr?.orgEntitlements?.[name]?.requireDescription
                                  }
                                >
                                  {({ getFieldValue: getVal }) => {
                                    const reqDesc = getVal(['orgEntitlements', name, 'requireDescription']);
                                    const descRequired = !isDeleted && reqDesc !== false;
                                    return (
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'description']}
                                        rules={[{ required: descRequired, message: 'Description is required' }, noLeadingTrailingSpaces]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Input placeholder="e.g., description" disabled={isDeleted} />
                                      </Form.Item>
                                    );
                                  }}
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                <Form.Item {...restField} name={[name, 'requireDescription']} valuePropName="checked" style={{ marginBottom: 0 }}>
                                  <Checkbox disabled={isDeleted} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'access_type']} rules={[{ required: !isDeleted, message: 'Required' }]} style={{ marginBottom: 0 }}>
                                  <select
                                    disabled={isDeleted}
                                    style={{ width: '100%', padding: '4px 8px', borderRadius: 6, border: '1px solid #d9d9d9' }}
                                  >
                                    <option value="">Select</option>
                                    <option value="R">(R) Read Only</option>
                                    <option value="RW">(RW) Read Write</option>
                                  </select>
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <Form.Item {...restField} name={[name, 'needs_quantity']} valuePropName="checked" style={{ marginBottom: 0 }}>
                                  <Checkbox disabled={isDeleted || (!isNewRow && isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'needs_quantity'))} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item {...restField} name={[name, 'unit']} style={{ marginBottom: 0 }} rules={[noLeadingTrailingSpaces]}>
                                  <Input placeholder="e.g., users" disabled={isDeleted} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <Form.Item {...restField} name={[name, 'is_service']} valuePropName="checked" style={{ marginBottom: 0 }}>
                                  <Checkbox disabled={isDeleted || (!isNewRow && isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'is_service'))} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: 0, width: 0, overflow: 'hidden' }}>
                                <Form.Item {...restField} name={[name, 'action']} style={{ marginBottom: 0 }} noStyle>
                                  <Input type="hidden" />
                                </Form.Item>
                                <Form.Item {...restField} name={[name, 'product_id']} style={{ marginBottom: 0 }} noStyle>
                                  <Input type="hidden" />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                {isDeleted ? (
                                  <UndoOutlined
                                    onClick={() => !(isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) && handleUndoDelete('orgEntitlements', name)}
                                    style={{
                                      color: (isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? '#d9d9d9' : '#1890ff',
                                      fontSize: 18,
                                      cursor: (isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? 'not-allowed' : 'pointer',
                                      opacity: (isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? 0.5 : 1
                                    }}
                                    title={(isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? 'Undo action disabled' : 'Undo delete'}
                                  />
                                ) : (
                                  <MinusCircleOutlined
                                    onClick={() => !(isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) && handleDeleteRow('orgEntitlements', name)}
                                    style={{
                                      color: (isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? '#d9d9d9' : '#ff4d4f',
                                      fontSize: 18,
                                      cursor: (isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? 'not-allowed' : 'pointer',
                                      opacity: (isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? 0.5 : 1
                                    }}
                                    title={(isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'delete') && !isNewRow) ? 'Delete action disabled' : 'Delete'}
                                  />
                                )}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  <Button
                    type="dashed"
                    onClick={() => handleAddRow('orgEntitlements', { name: '', value: '', description: '', access_type: '', needs_quantity: false, unit: '', is_service: false, product_id: productId, requireDescription: true })}
                    block
                    icon={<PlusOutlined />}
                    disabled={isFieldDisabled(EntityType.ORG_ENTITLEMENT, 'add')}
                  >
                    Add Org Entitlement
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}

        {!hasStandardEntitlements && !hasOrgEntitlements && (
          <Alert
            message="No Entitlements Found"
            description="This product has no entitlements to edit."
            type="warning"
            showIcon
          />
        )}
      </Form>
    </div>
  );
});

export default EntitlementTableEditor;
